import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import type { TenantHomePageContent } from '../../types/site-pages';
import { User, Star, Quote } from 'lucide-react';

interface Testimonial {
  id: string;
  name: string;
  profession: string;
  content: string;
  formattedContent?: string;
  rating: number;
  publishDate: {
    toDate: () => Date;
  };
}

/* Depoimentos estáticos de fallback para a landing page global */
const staticTestimonials: Testimonial[] = [
  {
    id: 'static-1',
    name: 'Dra. Mariana Costa',
    profession: 'Diretora Clínica — FisioVida',
    content:
      'O sistema de avaliações é o que mais impressiona. Antes eu gastava quase 2 horas para montar um laudo de TCPE com interpretação clínica. Agora o RevaHub gera tudo em segundos — com gráficos, zonas de treinamento e referências bibliográficas. É outro nível.',
    rating: 5,
    publishDate: { toDate: () => new Date() },
  },
  {
    id: 'static-2',
    name: 'Dr. Ricardo Almeida',
    profession: 'Fisioterapeuta Cardiorrespiratório — Centro Reabilitar',
    content:
      'As 13 avaliações disponíveis cobrem praticamente tudo que preciso no dia a dia: TC6, espirometria, CAT, SGRQ, HADS... E o melhor: cada laudo já vem com interpretação clínica automatizada usando equações validadas para a população brasileira. Nunca mais tive problemas em auditorias.',
    rating: 5,
    publishDate: { toDate: () => new Date() },
  },
  {
    id: 'static-3',
    name: 'Ana Paula Ferreira',
    profession: 'Gestora Administrativa — Clínica Movimento',
    content:
      'Além das avaliações incríveis, a integração entre agenda e faturamento é um divisor de águas. Antes, perdíamos cobranças de sessões realizadas. Hoje, cada atendimento gera fatura automaticamente. Reduzimos o tempo de fechamento mensal de 8 horas para menos de 2.',
    rating: 5,
    publishDate: { toDate: () => new Date() },
  },
];

interface TestimonialsSectionProps {
  mode?: 'global' | 'tenant';
  tenantContent?: TenantHomePageContent | null;
  tenantClientId?: string | null;
}

const TestimonialsSection = ({
  mode = 'tenant',
  tenantContent,
  tenantClientId,
}: TestimonialsSectionProps) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (mode === 'global') {
      /* Na landing page global, usa depoimentos estáticos */
      setTestimonials(staticTestimonials);
      setIsLoading(false);
      return;
    }

    const fetchTestimonials = async () => {
      if (!tenantClientId) {
        setTestimonials([]);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('testimonials')
          .select('*')
          .eq('clientId', tenantClientId)
          .eq('testimonialsStatus', 'published')
          .order('testimonialsPublishDate', { ascending: false })
          .limit(5);

        if (error) throw error;

        if (data && data.length > 0) {
          const testimonialsData = data
            .filter(
              (item: any) =>
                item && item.testimonialsStatus === 'published'
            )
            .map((item: any) => ({
              id: item.testimonialsId,
              name: item.testimonialsName,
              profession: item.testimonialsProfession,
              content: item.testimonialsContent,
              formattedContent: item.testimonialsFormattedContent,
              rating: item.testimonialsRating,
              publishDate: {
                toDate: () =>
                  item.testimonialsPublishDate
                    ? new Date(item.testimonialsPublishDate)
                    : new Date(),
              },
            })) as Testimonial[];

          setTestimonials(testimonialsData);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, [mode, tenantClientId]);

  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < rating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      ));
  };

  const isGlobal = mode === 'global';
  const tenantTitle = tenantContent?.testimonials?.title || 'O que nossos pacientes dizem';
  const tenantSubtitle =
    tenantContent?.testimonials?.subtitle ||
    'Histórias reais de pacientes que transformaram suas vidas com nossos tratamentos.';

  return (
    <section
      className={`py-20 md:py-28 overflow-hidden ${
        isGlobal ? 'bg-neutral-50' : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          {isGlobal && (
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-sm font-medium text-primary-700 mb-4"
            >
              Depoimentos
            </motion.span>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4"
          >
            {isGlobal
              ? 'Quem usa, recomenda'
              : tenantTitle}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-neutral-600 max-w-2xl mx-auto"
          >
            {isGlobal
              ? 'Veja como gestores e profissionais de saúde estão transformando suas clínicas com o RevaHub.'
              : tenantSubtitle}
          </motion.p>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="relative max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative p-6 rounded-xl shadow-sm border transition-all duration-300 hover:shadow-md ${
                    isGlobal
                      ? 'bg-white border-neutral-200'
                      : 'bg-neutral-50 border-transparent'
                  }`}
                >
                  {/* Ícone de aspas */}
                  {isGlobal && (
                    <Quote className="absolute top-4 right-4 h-8 w-8 text-primary-100" />
                  )}

                  <div className="flex items-center mb-4">
                    {renderStars(testimonial.rating)}
                  </div>

                  {testimonial.formattedContent ? (
                    <div
                      className="text-neutral-600 mb-6 italic leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: testimonial.formattedContent,
                      }}
                    />
                  ) : (
                    <p className="text-neutral-600 mb-6 italic leading-relaxed">
                      "{testimonial.content}"
                    </p>
                  )}

                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-neutral-500">
                        {testimonial.profession}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <div className="relative max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-neutral-50 p-8 md:p-12 rounded-xl shadow-sm text-center"
            >
              <p className="text-neutral-600">
                Depoimentos serão adicionados em breve.
              </p>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
